import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ImageLoadingSpinner, SubtleImageLoadingSpinner } from './LoadingSpinner';

interface GalleryImageProps {
    src: string;
    alt: string;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    onLoad?: () => void;
    onError?: () => void;
    preload?: boolean;
    showLoadingOverlay?: boolean;
    isThumbnail?: boolean;
    fetchPriority?: 'high' | 'low' | 'auto';
}

const ImageContainer = styled.div<{ $clickable?: boolean }>`
    position: relative;
    overflow: hidden;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    transition: opacity 0.3s ease-in-out;
`;

const StyledImage = styled.img<{ $loaded: boolean; $error: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${props => props.$loaded && !props.$error ? 1 : 0};
    transition: opacity 0.3s ease-in-out;
    display: block;
`;

const ErrorPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 14px;
    text-align: center;
    padding: 20px;
    border: 1px solid #dee2e6;
`;

export const GalleryImage: React.FC<GalleryImageProps> = ({
    src,
    alt,
    onClick,
    className,
    style,
    onLoad,
    onError,
    preload = false,
    showLoadingOverlay = true,
    isThumbnail = false,
    fetchPriority = 'auto',
}) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        if (!src) return;

        setLoaded(false);
        setError(false);
        setIsLoading(true);

        const img = new Image();
        
        img.onload = () => {
            setLoaded(true);
            setIsLoading(false);
            onLoad?.();
        };

        img.onerror = () => {
            setError(true);
            setIsLoading(false);
            onError?.();
        };

        img.src = src;
    }, [src, onLoad, onError]);

    const handleImageLoad = () => {
        setLoaded(true);
        setIsLoading(false);
        onLoad?.();
    };

    const handleImageError = () => {
        setError(true);
        setIsLoading(false);
        onError?.();
    };

    return (
        <ImageContainer 
            $clickable={!!onClick}
            onClick={onClick}
            className={className}
            style={style}
        >
            {error ? (
                <ErrorPlaceholder>
                    <div>
                        <div>⚠️</div>
                        <div>Failed to load image</div>
                    </div>
                </ErrorPlaceholder>
            ) : (
                <>
                    <StyledImage
                        ref={imgRef}
                        src={src}
                        alt={alt}
                        $loaded={loaded}
                        $error={error}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        loading={preload ? 'eager' : 'lazy'}
                        fetchPriority={fetchPriority}
                    />
                    {isLoading && showLoadingOverlay && (
                        isThumbnail ? (
                            <SubtleImageLoadingSpinner overlay={true} />
                        ) : (
                            <ImageLoadingSpinner overlay={true} />
                        )
                    )}
                </>
            )}
        </ImageContainer>
    );
};

// Specialized components for different use cases
export const ThumbnailImage: React.FC<Omit<GalleryImageProps, 'showLoadingOverlay' | 'isThumbnail'>> = (props) => (
    <GalleryImage {...props} showLoadingOverlay={true} isThumbnail={true} />
);

export const FullSizeImage: React.FC<GalleryImageProps> = (props) => (
    <GalleryImage {...props} showLoadingOverlay={true} />
);

export const PreloadableImage: React.FC<GalleryImageProps> = (props) => (
    <GalleryImage {...props} preload={true} />
);
